import { useEffect, useMemo } from 'react';
import Select from '@Common/Select';
import { useGeneratorContext } from '@Context/GeneratorContext';
import { JiraProject } from '@Models/types';
import { useAuth } from '@Context/CognitoAuthProvider';
import { jiraAPI } from '@Lib/axios/jira';
import PromptCrud from './prompt-crud/PromptCrud';

interface JiraProjectsProps {
  showPromptCrud?: boolean;
  onModeSwitch?: () => void;
}

const JiraProjects = ({
  showPromptCrud = false,
}: JiraProjectsProps) => {
  const { user, logout } = useAuth();
  const jira = useMemo(() => jiraAPI(logout), [logout]);
  const {
    jiraProjects,
    fetchJiraProjects,
    selectedJiraProject,
    setSelectedJiraProject,
    genPageLoading,
    setGenPageLoading,
  } = useGeneratorContext();

  const loading = genPageLoading;

  useEffect(() => {
    // Only fetch if the user is authenticated and the list is empty
    if (user?.profile && jiraProjects.length === 0) {
      setGenPageLoading(true);
      fetchJiraProjects().finally(() => setGenPageLoading(false));
    }
  }, [user, jiraProjects.length, setGenPageLoading]);

  const projectOptions = useMemo(
    () =>
      jiraProjects.map((proj) => ({
        label: `${proj.name} (${proj.key})`,
        value: proj,
      })),
    [jiraProjects]
  );

  // 2. Handle Change - No casting needed, Select now supports JiraProject
  const handleProjectChange = (
    selected: { label: string; value: JiraProject } | null
  ) => {
    setSelectedJiraProject(selected?.value || null);
  };

  const placeholder =
    loading && !selectedJiraProject
      ? 'Loading Jira Spaces...'
      : selectedJiraProject
        ? `${selectedJiraProject.name} (${selectedJiraProject.key})`
        : 'Select Jira Project';

  // define favorites
  const favorites = useMemo(
    () => ({
      enabled: true,
      type: 'jira-project' as const,
      fetch: jira.getFavProjects,
      save: jira.saveFavProjects,
      favoriteKey: 'key' as const,
    }),
    [jira]
  );

  return (
    <div data-testid="test-jira-projects" className="rounded-lg bg-white p-4">
      {showPromptCrud ? (
        <div className="grid grid-cols-2 gap-x-4">
          <div className="flex flex-col gap-y-5">
            <span className="font-extrabold text-primary-700">Projects</span>
            <Select
              type="jira-project" // Using the new type
              options={projectOptions}
              onChange={handleProjectChange}
              placeholder={placeholder}
              loading={loading}
              disabled={loading}
              favorites={favorites}
            />
          </div>
          <PromptCrud />
        </div>
      ) : (
        <div className="flex flex-col gap-y-2">
          <span className="font-extrabold text-primary-700">Jira Projects</span>
          <Select
            type="jira-project" // Using the new type
            options={projectOptions}
            onChange={handleProjectChange}
            placeholder={placeholder}
            loading={loading}
            disabled={loading}
            favorites={favorites}
          />
        </div>
      )}
    </div>
  );
};

export default JiraProjects;
